import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Star,
  Gift,
  Zap,
  Ticket,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Clock,
  TrendingUp,
  Award,
  ShoppingBag,
  RefreshCw,
  Tag,
  Trophy,
  Calendar,
  Users,
  Copy,
  Share2,
  Lock,
  Play,
  ChevronDown,
  ChevronUp,
  Target,
} from 'lucide-react';
import { api } from '../lib/api';
import { useBranding } from '../contexts/BrandingContext';
import { AppLayout } from '../components/AppLayout';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LoyaltyTier {
  id: string;
  code: string;
  name: string;
  minPoints: number;
  color: string;
  rank: number;
}
interface LoyaltyReward {
  id: string;
  name: string;
  description: string | null;
  type: string;
  pointsCost: number;
  value: { amount: number; unit: 'percent' | 'fixed' | 'free' } | null;
  availableQuantity: number | null;
  redeemedCount: number;
  imageUrl: string | null;
}
interface LedgerEntry {
  id: string;
  type: 'earned' | 'redeemed' | 'expired' | 'adjusted' | 'bonus';
  points: number;
  balanceAfter: number;
  referenceType: string | null;
  description: string | null;
  occurredAt: string;
}
interface LoyaltyAccount {
  id: string;
  programId: string;
  pointsBalance: number;
  lifetimePoints: number;
  tierCode: string | null;
  enrolledAt: string;
  programName: string | null;
  programSlug: string | null;
  tiers: LoyaltyTier[];
  currentTierDetails: LoyaltyTier | null;
  nextTierDetails: LoyaltyTier | null;
  pointsToNextTier: number | null;
  rewards: LoyaltyReward[];
  ledger: LedgerEntry[];
}
interface Coupon {
  id: string;
  code: string;
  type: string;
  value: { amount: number; unit: string };
  status: string;
  issuedAt: string;
  expiresAt: string | null;
}
interface Redemption {
  id: string;
  accountId: string;
  rewardName: string | null;
  pointsCost: number;
  status: string;
  redeemedAt: string;
  fulfilledAt: string | null;
}
interface LoyaltyData {
  enrolled: boolean;
  accounts: LoyaltyAccount[];
  coupons: Coupon[];
  redemptions: Redemption[];
}

// Editorial types
interface InterestTopic {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  iconUrl: string | null;
  isActive: boolean;
}
interface InterestCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  isActive: boolean;
  topics: InterestTopic[];
}
interface CustomerInterests {
  categories: string[];
  topics: string[];
}
interface InterestsData {
  categories: InterestCategory[];
  customerInterests: CustomerInterests;
}

interface BadgeProgress {
  id: string;
  code: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  conditionType: string;
  conditionThreshold: number;
  benefitType: string;
  benefitValue: string | null;
  benefitDurationDays: number | null;
  unlocked: boolean;
  unlockedAt: string | null;
  currentProgress: number;
  progressPercent: number;
}

interface WeeklyContest {
  id: string;
  programId: string;
  weekStart: string;
  weekEnd: string;
  gameType: string;
  prizeType: string;
  prizeDescription: string;
  prizeQuantity: number | null;
  consolationPoints: number;
  status: string;
  playsAvailable: number;
  playsUsed: number;
  playsRemaining: number;
}
interface ContestPlayResult {
  won: boolean;
  prize: string | null;
  consolationPoints: number;
  message: string;
}

interface SpecialContest {
  id: string;
  programId: string;
  title: string;
  description: string | null;
  prize: string;
  prizeType: string;
  startDate: string;
  endDate: string;
  entryRequirement: string | null;
  minPointsRequired: number | null;
  requiredBadgeCode: string | null;
  maxEntries: number | null;
  status: string;
  hasEntered: boolean;
  canEnter: boolean;
  entryBlockReason: string | null;
}

interface LeaderboardEntry {
  customerId: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  score: number;
  rank: number;
  previousRank: number | null;
  deltaRank: number | null;
  isCurrentUser?: boolean;
}
interface LeaderboardData {
  entries: LeaderboardEntry[];
  currentUserEntry: LeaderboardEntry | null;
}

interface ReferralData {
  referralCode: string | null;
  referralCount: number;
  referrals: { id: string; createdAt: string; firstName: string | null; lastName: string | null }[];
  referredByCode: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string) {
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
function fmtYear(d: string) {
  return new Date(d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
function initials(first: string | null, last: string | null) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '?';
}
function fullName(first: string | null, last: string | null) {
  return `${first ?? ''} ${last ?? ''}`.trim() || 'Anonymous';
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function TierBadge({ tier, small = false }: { tier: LoyaltyTier; small?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${small ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'}`}
      style={{ backgroundColor: tier.color + '20', color: tier.color }}
    >
      <Award size={small ? 10 : 13} />
      {tier.name}
    </span>
  );
}

function LedgerTypeBadge({ type }: { type: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    earned: { cls: 'bg-emerald-50 text-emerald-700', label: 'Earned' },
    bonus: { cls: 'bg-blue-50 text-blue-700', label: 'Bonus' },
    redeemed: { cls: 'bg-orange-50 text-orange-700', label: 'Redeemed' },
    expired: { cls: 'bg-zinc-100 text-zinc-500', label: 'Expired' },
    adjusted: { cls: 'bg-purple-50 text-purple-700', label: 'Adjusted' },
  };
  const { cls, label } = map[type] ?? { cls: 'bg-zinc-100 text-zinc-500', label: type };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-base font-semibold text-zinc-900 mb-3">{children}</h2>;
}

function EmptyCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center bg-white border border-zinc-100 rounded-2xl">
      <div className="w-12 h-12 rounded-2xl bg-zinc-50 flex items-center justify-center text-zinc-300">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-zinc-700">{title}</p>
        <p className="text-xs text-zinc-400 mt-0.5 max-w-[240px]">{description}</p>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 size={22} className="animate-spin text-zinc-400" />
    </div>
  );
}

// ─── TierProgress ─────────────────────────────────────────────────────────────

function TierProgress({
  account,
  branding,
}: {
  account: LoyaltyAccount;
  branding: { primaryColor: string };
}) {
  const { pointsBalance, currentTierDetails, nextTierDetails, pointsToNextTier, tiers } = account;
  const prevMin = currentTierDetails?.minPoints ?? 0;
  const nextMin = nextTierDetails?.minPoints ?? null;
  const progress =
    nextMin !== null
      ? Math.min(100, Math.round(((pointsBalance - prevMin) / (nextMin - prevMin)) * 100))
      : 100;

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div>
          <p className="text-4xl font-extrabold text-zinc-900 tracking-tight">
            {pointsBalance.toLocaleString()}
          </p>
          <p className="text-sm text-zinc-500 mt-0.5">points available</p>
        </div>
        {currentTierDetails && <TierBadge tier={currentTierDetails} />}
      </div>
      {nextTierDetails && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{currentTierDetails?.name ?? 'Standard'}</span>
            <span>
              {(pointsToNextTier ?? 0).toLocaleString()} pts to{' '}
              <strong style={{ color: nextTierDetails.color }}>{nextTierDetails.name}</strong>
            </span>
          </div>
          <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progress}%`, backgroundColor: branding.primaryColor }}
            />
          </div>
        </div>
      )}
      {tiers.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {tiers.map((t) => (
            <TierBadge key={t.id} tier={t} small />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RewardCard ───────────────────────────────────────────────────────────────

function RewardCard({
  reward,
  balance,
  onRedeem,
  redeeming,
}: {
  reward: LoyaltyReward;
  balance: number;
  onRedeem: (reward: LoyaltyReward) => void;
  redeeming: boolean;
}) {
  const canAfford = balance >= reward.pointsCost;
  const outOfStock =
    reward.availableQuantity !== null && reward.redeemedCount >= reward.availableQuantity;
  const valueLabel = reward.value
    ? reward.value.unit === 'percent'
      ? `${reward.value.amount}% off`
      : reward.value.unit === 'free'
        ? 'Free item'
        : `$${reward.value.amount} off`
    : null;
  const typeIcon: Record<string, React.ReactNode> = {
    discount: <Zap size={14} />,
    free_item: <Gift size={14} />,
    shipping: <ShoppingBag size={14} />,
    custom: <Star size={14} />,
  };

  return (
    <div
      className={`bg-white border rounded-2xl p-4 shadow-sm flex flex-col gap-3 transition-opacity ${!canAfford || outOfStock ? 'opacity-60' : ''}`}
    >
      {reward.imageUrl ? (
        <img
          src={reward.imageUrl}
          alt={reward.name}
          className="w-full h-28 object-cover rounded-xl bg-zinc-100"
        />
      ) : (
        <div className="w-full h-28 rounded-xl bg-zinc-50 flex items-center justify-center text-zinc-300">
          <Gift size={32} />
        </div>
      )}
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
          {typeIcon[reward.type] ?? <Star size={12} />}
          <span className="capitalize">{reward.type.replace('_', ' ')}</span>
          {valueLabel && (
            <>
              <span className="text-zinc-300">·</span>
              <span className="font-medium text-zinc-700">{valueLabel}</span>
            </>
          )}
        </div>
        <p className="text-sm font-semibold text-zinc-900 leading-snug">{reward.name}</p>
        {reward.description && (
          <p className="text-xs text-zinc-400 leading-snug">{reward.description}</p>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-zinc-900 flex items-center gap-1">
          <Star size={13} className="text-amber-500" />
          {reward.pointsCost.toLocaleString()} pts
        </span>
        {outOfStock ? (
          <span className="text-xs text-zinc-400 font-medium">Out of stock</span>
        ) : (
          <button
            onClick={() => onRedeem(reward)}
            disabled={!canAfford || redeeming}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              canAfford
                ? 'bg-[--color-primary] text-white hover:opacity-90'
                : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
            }`}
          >
            {redeeming ? <Loader2 size={12} className="animate-spin" /> : 'Redeem'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({
  data,
  account,
  branding,
  redeemingId,
  redeemMsg,
  onRedeem,
  onDismissMsg,
  onSwitchTab,
}: {
  data: LoyaltyData;
  account: LoyaltyAccount;
  branding: { primaryColor: string };
  redeemingId: string | null;
  redeemMsg: { ok: boolean; text: string } | null;
  onRedeem: (r: LoyaltyReward, a: LoyaltyAccount) => void;
  onDismissMsg: () => void;
  onSwitchTab: (t: TabKey) => void;
}) {
  return (
    <div className="space-y-8">
      {/* Tier progress card */}
      <div className="bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-4">
          {account.programName ?? 'Loyalty Program'}
        </p>
        <TierProgress account={account} branding={branding} />
        <p className="text-xs text-zinc-400 mt-4">
          Member since{' '}
          {new Date(account.enrolledAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
          })}
          {' · '}
          {account.lifetimePoints.toLocaleString()} lifetime pts
        </p>
      </div>

      {/* Redeem feedback */}
      {redeemMsg && (
        <div
          className={`rounded-xl px-5 py-3 text-sm flex items-center gap-2 ${
            redeemMsg.ok
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}
        >
          {redeemMsg.ok ? <CheckCircle2 size={16} /> : null}
          {redeemMsg.text}
          <button onClick={onDismissMsg} className="ml-auto text-xs opacity-60 hover:opacity-100">
            ✕
          </button>
        </div>
      )}

      {/* Quick links to editorial sections */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            tab: 'contest' as TabKey,
            icon: <Play size={18} />,
            label: 'Gioca & Vinci',
            color: 'bg-purple-50 text-purple-600',
          },
          {
            tab: 'badges' as TabKey,
            icon: <Award size={18} />,
            label: 'Badges',
            color: 'bg-amber-50 text-amber-600',
          },
          {
            tab: 'leaderboard' as TabKey,
            icon: <Trophy size={18} />,
            label: 'Classifica',
            color: 'bg-emerald-50 text-emerald-600',
          },
          {
            tab: 'referral' as TabKey,
            icon: <Users size={18} />,
            label: 'Referral',
            color: 'bg-sky-50 text-sky-600',
          },
        ].map(({ tab, icon, label, color }) => (
          <button
            key={tab}
            onClick={() => onSwitchTab(tab)}
            className="flex flex-col items-center gap-2 py-4 bg-white border border-zinc-100 rounded-2xl shadow-sm hover:shadow-md hover:border-zinc-200 transition-all"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
              {icon}
            </div>
            <span className="text-xs font-medium text-zinc-700">{label}</span>
          </button>
        ))}
      </div>

      {/* Rewards catalog */}
      {account.rewards.length > 0 && (
        <section>
          <SectionTitle>Rewards catalog</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {account.rewards.map((r) => (
              <RewardCard
                key={r.id}
                reward={r}
                balance={account.pointsBalance}
                onRedeem={(rw) => onRedeem(rw, account)}
                redeeming={redeemingId === r.id}
              />
            ))}
          </div>
        </section>
      )}

      {/* Active coupons */}
      {data.coupons.length > 0 && (
        <section>
          <SectionTitle>Your coupons</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.coupons.map((c) => {
              const valueLabel =
                c.value.unit === 'percent'
                  ? `${c.value.amount}% off`
                  : c.value.unit === 'free'
                    ? 'Free item'
                    : `$${c.value.amount} off`;
              return (
                <div
                  key={c.id}
                  className="bg-white border border-dashed border-amber-300 rounded-2xl p-4 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500">
                      <Ticket size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-900 font-mono tracking-widest">
                        {c.code}
                      </p>
                      <p className="text-xs text-zinc-500">{valueLabel}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {c.expiresAt ? (
                      <p className="text-xs text-zinc-400">Expires {fmt(c.expiresAt)}</p>
                    ) : (
                      <p className="text-xs text-zinc-400">No expiry</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Two-column: ledger + redemptions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {account.ledger.length > 0 && (
          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3 flex items-center gap-2">
              <TrendingUp size={16} className="text-zinc-400" />
              Points history
            </h2>
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50">
              {account.ledger.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <LedgerTypeBadge type={entry.type} />
                    <p className="text-xs text-zinc-500 truncate">
                      {entry.description ??
                        (entry.referenceType ? entry.referenceType.replace('_', ' ') : entry.type)}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p
                      className={`text-sm font-bold ${entry.points >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}
                    >
                      {entry.points >= 0 ? '+' : ''}
                      {entry.points.toLocaleString()}
                    </p>
                    <p className="text-xs text-zinc-400">{fmt(entry.occurredAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        {data.redemptions.length > 0 && (
          <section>
            <h2 className="text-base font-semibold text-zinc-900 mb-3 flex items-center gap-2">
              <ChevronRight size={16} className="text-zinc-400" />
              Redemption history
            </h2>
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50">
              {data.redemptions.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">
                      {r.rewardName ?? 'Reward'}
                    </p>
                    <p className="text-xs text-zinc-400">{fmtYear(r.redeemedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <span className="text-sm font-bold text-orange-600">
                      −{r.pointsCost.toLocaleString()} pts
                    </span>
                    {r.status === 'fulfilled' ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                        <CheckCircle2 size={12} /> Done
                      </span>
                    ) : r.status === 'pending' ? (
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-400 font-medium">
                        <Clock size={12} /> Pending
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400 capitalize">{r.status}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── InterestsTab ─────────────────────────────────────────────────────────────

function InterestsTab({ programId }: { programId: string }) {
  const [data, setData] = useState<InterestsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  useEffect(() => {
    api
      .get<InterestsData>(`/customer/loyalty/interests?programId=${programId}`)
      .then((r) => {
        setData(r.data);
        setSelectedCategories(new Set(r.data.customerInterests.categories));
        setSelectedTopics(new Set(r.data.customerInterests.topics));
        // Auto-expand categories that have selected topics
        const toExpand = new Set<string>();
        r.data.customerInterests.topics.forEach((tid) => {
          const cat = r.data.categories.find((c) => c.topics.some((t) => t.id === tid));
          if (cat) toExpand.add(cat.id);
        });
        setExpandedCats(toExpand);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [programId]);

  const toggleCat = (id: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleTopic = (id: string) => {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleExpand = (id: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/customer/loyalty/interests', {
        categoryIds: [...selectedCategories],
        topicIds: [...selectedTopics],
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (!data)
    return (
      <EmptyCard
        icon={<Tag size={22} />}
        title="No categories available"
        description="Interest categories will appear here once configured."
      />
    );

  const activeCategories = data.categories.filter((c) => c.isActive);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-zinc-500">
          Select the topics you care about to receive personalised content and earn bonus points.
        </p>
      </div>

      <div className="space-y-3">
        {activeCategories.map((cat) => {
          const catSelected = selectedCategories.has(cat.id);
          const isExpanded = expandedCats.has(cat.id);
          const activeTopics = cat.topics.filter((t) => t.isActive);
          const selectedTopicCount = activeTopics.filter((t) => selectedTopics.has(t.id)).length;

          return (
            <div
              key={cat.id}
              className="bg-white border border-zinc-100 rounded-2xl overflow-hidden shadow-sm"
            >
              {/* Category header */}
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  onClick={() => toggleCat(cat.id)}
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    catSelected
                      ? 'bg-[--color-primary] border-[--color-primary]'
                      : 'border-zinc-300 hover:border-zinc-400'
                  }`}
                  aria-label={`Toggle ${cat.name}`}
                >
                  {catSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>

                {cat.iconUrl ? (
                  <img
                    src={cat.iconUrl}
                    alt={cat.name}
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                ) : (
                  <div className="w-6 h-6 rounded bg-zinc-100 flex items-center justify-center text-zinc-400 flex-shrink-0">
                    <Tag size={13} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900">{cat.name}</p>
                  {cat.description && (
                    <p className="text-xs text-zinc-400 truncate">{cat.description}</p>
                  )}
                </div>

                {activeTopics.length > 0 && (
                  <button
                    onClick={() => toggleExpand(cat.id)}
                    className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 transition-colors ml-2"
                  >
                    {selectedTopicCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-[--color-primary]/10 text-[--color-primary] text-[10px] font-medium">
                        {selectedTopicCount}
                      </span>
                    )}
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
              </div>

              {/* Topics */}
              {isExpanded && activeTopics.length > 0 && (
                <div className="border-t border-zinc-50 px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {activeTopics.map((topic) => {
                      const topicSelected = selectedTopics.has(topic.id);
                      return (
                        <button
                          key={topic.id}
                          onClick={() => toggleTopic(topic.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            topicSelected
                              ? 'bg-[--color-primary] text-white border-[--color-primary]'
                              : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:border-zinc-300'
                          }`}
                        >
                          {topic.iconUrl && (
                            <img
                              src={topic.iconUrl}
                              alt=""
                              className="w-3.5 h-3.5 object-contain"
                            />
                          )}
                          {topic.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2.5 rounded-xl bg-[--color-primary] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Save preferences
        </button>
        {saved && (
          <span className="text-sm text-emerald-600 flex items-center gap-1.5">
            <CheckCircle2 size={15} /> Saved!
          </span>
        )}
      </div>
    </div>
  );
}

// ─── ContestTab ───────────────────────────────────────────────────────────────

function ContestTab({ programId, balance }: { programId: string; balance: number }) {
  const [section, setSection] = useState<'weekly' | 'special'>('weekly');
  const [weekly, setWeekly] = useState<WeeklyContest | null>(null);
  const [weeklyLoaded, setWeeklyLoaded] = useState(false);
  const [specials, setSpecials] = useState<SpecialContest[] | null>(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [specialsLoading, setSpecialsLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playResult, setPlayResult] = useState<ContestPlayResult | null>(null);
  const [entering, setEntering] = useState<string | null>(null);
  const [enterMsg, setEnterMsg] = useState<{ id: string; ok: boolean; text: string } | null>(null);
  // Wheel animation
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelAngle, setWheelAngle] = useState(0);
  const wheelRef = useRef<number>(0);

  useEffect(() => {
    if (section === 'weekly') {
      setWeeklyLoading(true);
      setWeeklyLoaded(false);
      api
        .get<WeeklyContest>(`/customer/loyalty/contests/weekly/current?programId=${programId}`)
        .then((r) => setWeekly(r.data))
        .catch(() => setWeekly(null))
        .finally(() => {
          setWeeklyLoading(false);
          setWeeklyLoaded(true);
        });
    }
  }, [section, programId]);

  useEffect(() => {
    if (section === 'special') {
      setSpecialsLoading(true);
      api
        .get<SpecialContest[]>(`/customer/loyalty/contests/special?programId=${programId}`)
        .then((r) => setSpecials(r.data))
        .catch(() => setSpecials([]))
        .finally(() => setSpecialsLoading(false));
    }
  }, [section, programId]);

  const handlePlay = async () => {
    if (!weekly) return;
    setPlaying(true);
    setPlayResult(null);
    // Spin animation
    setWheelSpinning(true);
    const spins = 3 + Math.random() * 3;
    const targetAngle = wheelRef.current + spins * 360;
    wheelRef.current = targetAngle % 360;
    setWheelAngle(targetAngle);
    try {
      await new Promise((r) => setTimeout(r, 1800)); // wait for spin
      const res = await api.post<ContestPlayResult>(
        `/customer/loyalty/contests/weekly/${weekly.id}/play`,
        { programId },
      );
      setPlayResult(res.data);
      // Refresh weekly state
      api
        .get<WeeklyContest>(`/customer/loyalty/contests/weekly/current?programId=${programId}`)
        .then((r) => setWeekly(r.data))
        .catch(() => {});
    } catch {
      setPlayResult({
        won: false,
        prize: null,
        consolationPoints: 0,
        message: 'Something went wrong. Please try again.',
      });
    } finally {
      setPlaying(false);
      setWheelSpinning(false);
    }
  };

  const handleEnter = async (contestId: string) => {
    setEntering(contestId);
    setEnterMsg(null);
    try {
      await api.post(`/customer/loyalty/contests/special/${contestId}/enter`, { programId });
      setEnterMsg({ id: contestId, ok: true, text: 'You have entered the contest!' });
      // Refresh specials
      api
        .get<SpecialContest[]>(`/customer/loyalty/contests/special?programId=${programId}`)
        .then((r) => setSpecials(r.data))
        .catch(() => {});
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not enter contest';
      const friendly: Record<string, string> = {
        already_entered: 'You have already entered this contest.',
        insufficient_points: `You need at least ${(specials?.find((c) => c.id === contestId)?.minPointsRequired ?? 0).toLocaleString()} points.`,
        missing_badge: 'You need a specific badge to enter this contest.',
        contest_closed: 'This contest is no longer accepting entries.',
        contest_full: 'This contest has reached its maximum entries.',
      };
      setEnterMsg({ id: contestId, ok: false, text: friendly[msg] ?? msg });
    } finally {
      setEntering(null);
    }
  };

  // Wheel segments colours
  const WHEEL_COLORS = [
    '#f59e0b',
    '#6366f1',
    '#10b981',
    '#ef4444',
    '#3b82f6',
    '#8b5cf6',
    '#f97316',
    '#14b8a6',
  ];
  const segments = 8;

  return (
    <div className="space-y-6">
      {/* Section switcher */}
      <div className="flex gap-1 p-1 bg-zinc-100 rounded-xl w-fit">
        {(['weekly', 'special'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSection(s)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              section === s
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {s === 'weekly' ? 'Gioca & Vinci' : 'Special Contests'}
          </button>
        ))}
      </div>

      {/* Weekly game */}
      {section === 'weekly' && (
        <>
          {weeklyLoading ? (
            <Spinner />
          ) : weeklyLoaded && !weekly ? (
            <EmptyCard
              icon={<Calendar size={22} />}
              title="No active contest"
              description="There is no active weekly contest at the moment. Check back soon!"
            />
          ) : weekly ? (
            <div className="space-y-6">
              {/* Prize info */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-purple-400 mb-1">
                      Weekly contest
                    </p>
                    <p className="text-lg font-bold text-purple-900">
                      {weekly.prizeDescription || 'Win exciting prizes!'}
                    </p>
                    <p className="text-sm text-purple-700 mt-1">
                      {fmt(weekly.weekStart)} — {fmt(weekly.weekEnd)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-purple-400 mb-0.5">Consolation</p>
                    <p className="text-xl font-extrabold text-purple-700">
                      +{weekly.consolationPoints}
                    </p>
                    <p className="text-xs text-purple-400">pts / play</p>
                  </div>
                </div>
              </div>

              {/* Plays counter */}
              <div className="flex items-center justify-between bg-white border border-zinc-100 rounded-2xl px-5 py-3 shadow-sm">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-wide font-medium">
                    Plays remaining
                  </p>
                  <p className="text-3xl font-extrabold text-zinc-900 mt-0.5">
                    {weekly.playsRemaining === -1 ? '∞' : weekly.playsRemaining}
                    {weekly.playsAvailable !== -1 && (
                      <span className="text-base text-zinc-400 font-normal ml-1">
                        / {weekly.playsAvailable}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  {weekly.playsAvailable > 0 && Array.from({ length: weekly.playsAvailable }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-3 h-3 rounded-full ${i < weekly.playsUsed ? 'bg-zinc-200' : 'bg-[--color-primary]'}`}
                    />
                  ))}
                </div>
              </div>

              {/* Wheel of fortune */}
              <div className="flex flex-col items-center gap-6">
                <div className="relative w-52 h-52">
                  {/* Pointer */}
                  <div
                    className="absolute top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-16 border-l-transparent border-r-transparent border-b-zinc-800 z-10"
                    style={{ borderBottomWidth: 20 }}
                  />
                  {/* Wheel */}
                  <svg
                    viewBox="0 0 200 200"
                    className="w-full h-full drop-shadow-md"
                    style={{
                      transform: `rotate(${wheelAngle}deg)`,
                      transition: wheelSpinning
                        ? 'transform 1.8s cubic-bezier(0.17, 0.67, 0.12, 0.99)'
                        : 'none',
                    }}
                  >
                    {Array.from({ length: segments }).map((_, i) => {
                      const startAngle = (i * 360) / segments;
                      const endAngle = ((i + 1) * 360) / segments;
                      const startRad = ((startAngle - 90) * Math.PI) / 180;
                      const endRad = ((endAngle - 90) * Math.PI) / 180;
                      const x1 = 100 + 90 * Math.cos(startRad);
                      const y1 = 100 + 90 * Math.sin(startRad);
                      const x2 = 100 + 90 * Math.cos(endRad);
                      const y2 = 100 + 90 * Math.sin(endRad);
                      const midRad = (((startAngle + endAngle) / 2 - 90) * Math.PI) / 180;
                      const tx = 100 + 58 * Math.cos(midRad);
                      const ty = 100 + 58 * Math.sin(midRad);
                      return (
                        <g key={i}>
                          <path
                            d={`M 100 100 L ${x1} ${y1} A 90 90 0 0 1 ${x2} ${y2} Z`}
                            fill={WHEEL_COLORS[i % WHEEL_COLORS.length]}
                          />
                          <text
                            x={tx}
                            y={ty}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="10"
                            fill="white"
                            fontWeight="bold"
                            transform={`rotate(${startAngle + 360 / segments / 2}, ${tx}, ${ty})`}
                          >
                            {i === 0 ? '🏆' : i % 2 === 0 ? `+${weekly.consolationPoints}` : '★'}
                          </text>
                        </g>
                      );
                    })}
                    <circle cx="100" cy="100" r="12" fill="white" />
                    <circle cx="100" cy="100" r="8" fill="#71717a" />
                  </svg>
                </div>

                <button
                  onClick={handlePlay}
                  disabled={playing || weekly.playsRemaining === 0}
                  className={`px-8 py-3 rounded-2xl text-base font-bold transition-all ${
                    (weekly.playsRemaining === -1 || weekly.playsRemaining > 0) && !playing
                      ? 'bg-[--color-primary] text-white hover:opacity-90 shadow-lg hover:shadow-xl active:scale-95'
                      : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                  }`}
                >
                  {playing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" /> Spinning…
                    </span>
                  ) : weekly.playsRemaining === 0 ? (
                    'No plays remaining'
                  ) : (
                    <span className="flex items-center gap-2">
                      <Play size={16} /> Spin the wheel
                    </span>
                  )}
                </button>
              </div>

              {/* Play result */}
              {playResult && (
                <div
                  className={`rounded-2xl px-5 py-4 text-center border ${
                    playResult.won
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}
                >
                  <p className="text-2xl mb-1">{playResult.won ? '🎉' : '🎁'}</p>
                  <p className="font-bold text-zinc-900">{playResult.message}</p>
                  {playResult.consolationPoints > 0 && !playResult.won && (
                    <p className="text-sm text-emerald-700 mt-1">
                      +{playResult.consolationPoints} points credited to your account!
                    </p>
                  )}
                  <button
                    onClick={() => setPlayResult(null)}
                    className="mt-3 text-xs text-zinc-400 hover:text-zinc-600 underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </>
      )}

      {/* Special contests */}
      {section === 'special' && (
        <>
          {specialsLoading ? (
            <Spinner />
          ) : !specials || specials.length === 0 ? (
            <EmptyCard
              icon={<Target size={22} />}
              title="No special contests"
              description="Special contests will appear here when available."
            />
          ) : (
            <div className="space-y-4">
              {specials.map((c) => {
                const myMsg = enterMsg?.id === c.id ? enterMsg : null;
                return (
                  <div
                    key={c.id}
                    className="bg-white border border-zinc-100 rounded-2xl p-5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-bold text-zinc-900">{c.title}</p>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                              c.status === 'active'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-zinc-100 text-zinc-500'
                            }`}
                          >
                            {c.status}
                          </span>
                        </div>
                        {c.description && (
                          <p className="text-sm text-zinc-500 mb-2">{c.description}</p>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Trophy size={11} className="text-amber-500" /> {c.prize}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={11} /> {fmt(c.startDate)} — {fmt(c.endDate)}
                          </span>
                          {c.minPointsRequired != null && (
                            <span className="flex items-center gap-1">
                              <Star size={11} className="text-amber-500" />{' '}
                              {c.minPointsRequired.toLocaleString()} pts required
                            </span>
                          )}
                          {c.requiredBadgeCode && (
                            <span className="flex items-center gap-1">
                              <Award size={11} /> Badge: {c.requiredBadgeCode}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0">
                        {c.hasEntered ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-100 text-emerald-700">
                            <CheckCircle2 size={12} /> Entered
                          </span>
                        ) : !c.canEnter ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold bg-zinc-100 text-zinc-400">
                            <Lock size={12} /> {c.entryBlockReason ?? 'Locked'}
                          </span>
                        ) : (
                          <button
                            onClick={() => handleEnter(c.id)}
                            disabled={entering === c.id}
                            className="px-4 py-2 rounded-xl text-sm font-semibold bg-[--color-primary] text-white hover:opacity-90 disabled:opacity-60 transition-opacity flex items-center gap-1.5"
                          >
                            {entering === c.id ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Target size={13} />
                            )}
                            Enter
                          </button>
                        )}
                      </div>
                    </div>
                    {myMsg && (
                      <p
                        className={`mt-3 text-xs ${myMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}
                      >
                        {myMsg.ok ? <CheckCircle2 size={12} className="inline mr-1" /> : null}
                        {myMsg.text}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── BadgesTab ────────────────────────────────────────────────────────────────

const BADGE_CONDITION_LABELS: Record<string, string> = {
  points_total: 'total points',
  streak_days: 'streak days',
  articles_read: 'articles read',
  contests_played: 'contests played',
  referrals_completed: 'referrals completed',
  badges_count: 'badges collected',
};

function BadgesTab({ programId }: { programId: string }) {
  const [badges, setBadges] = useState<BadgeProgress[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<BadgeProgress[]>(`/customer/loyalty/badges?programId=${programId}`)
      .then((r) => setBadges(r.data))
      .catch(() => setBadges([]))
      .finally(() => setLoading(false));
  }, [programId]);

  if (loading) return <Spinner />;
  if (!badges || badges.length === 0)
    return (
      <EmptyCard
        icon={<Award size={22} />}
        title="No badges available"
        description="Badges will appear here once configured for this program."
      />
    );

  const unlocked = badges.filter((b) => b.unlocked);
  const locked = badges.filter((b) => !b.unlocked);

  const BadgeCard = ({ b }: { b: BadgeProgress }) => (
    <div
      className={`relative bg-white border rounded-2xl p-4 shadow-sm flex flex-col items-center gap-3 text-center transition-opacity ${b.unlocked ? '' : 'opacity-70'}`}
    >
      {b.unlocked && (
        <div className="absolute top-2 right-2">
          <CheckCircle2 size={14} className="text-emerald-500" />
        </div>
      )}
      {/* Icon */}
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center ${b.unlocked ? 'bg-amber-100' : 'bg-zinc-100'}`}
      >
        {b.iconUrl ? (
          <img src={b.iconUrl} alt={b.name} className="w-8 h-8 object-contain" />
        ) : (
          <Award size={26} className={b.unlocked ? 'text-amber-500' : 'text-zinc-300'} />
        )}
        {!b.unlocked && <Lock size={12} className="absolute text-zinc-400" />}
      </div>
      <div className="space-y-0.5">
        <p className="text-sm font-semibold text-zinc-900">{b.name}</p>
        {b.description && <p className="text-xs text-zinc-400">{b.description}</p>}
      </div>
      {b.unlocked ? (
        <p className="text-xs text-emerald-600 font-medium">
          {b.unlockedAt ? `Unlocked ${fmt(b.unlockedAt)}` : 'Unlocked'}
        </p>
      ) : (
        <div className="w-full space-y-1">
          <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-[--color-primary] transition-all"
              style={{ width: `${b.progressPercent}%` }}
            />
          </div>
          <p className="text-[10px] text-zinc-400">
            {b.currentProgress.toLocaleString()} / {b.conditionThreshold.toLocaleString()}{' '}
            {BADGE_CONDITION_LABELS[b.conditionType] ?? b.conditionType}
          </p>
        </div>
      )}
      {b.benefitType !== 'none' && b.unlocked && (
        <div className="w-full px-2.5 py-1.5 rounded-lg bg-amber-50 text-xs text-amber-700 font-medium">
          Benefit: {b.benefitValue ?? b.benefitType}
          {b.benefitDurationDays ? ` (${b.benefitDurationDays}d)` : ''}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {unlocked.length > 0 && (
        <section>
          <SectionTitle>Unlocked badges ({unlocked.length})</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {unlocked.map((b) => (
              <BadgeCard key={b.id} b={b} />
            ))}
          </div>
        </section>
      )}
      {locked.length > 0 && (
        <section>
          <SectionTitle>In progress ({locked.length})</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {locked.map((b) => (
              <BadgeCard key={b.id} b={b} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ─── LeaderboardTab ───────────────────────────────────────────────────────────

function LeaderboardTab({ programId }: { programId: string }) {
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('monthly');
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get<LeaderboardData>(
        `/customer/loyalty/leaderboard?programId=${programId}&periodType=${period}&limit=20`,
      )
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], currentUserEntry: null }))
      .finally(() => setLoading(false));
  }, [programId, period]);

  const delta = (d: number | null) => {
    if (d === null || d === 0) return <span className="text-zinc-300 text-[10px]">—</span>;
    if (d > 0) return <span className="text-emerald-600 text-[10px] font-bold">▲{d}</span>;
    return <span className="text-rose-500 text-[10px] font-bold">▼{Math.abs(d)}</span>;
  };

  const myEntry = data?.currentUserEntry;
  const entries = data?.entries ?? [];

  return (
    <div className="space-y-5">
      {/* Period switcher */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">Top 20 members ranked by points earned.</p>
        <div className="flex gap-1 p-1 bg-zinc-100 rounded-xl">
          {(['weekly', 'monthly'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors capitalize ${
                period === p
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Your rank card */}
      {myEntry && (
        <div className="bg-[--color-primary]/5 border border-[--color-primary]/20 rounded-2xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[--color-primary] flex items-center justify-center text-white text-sm font-bold">
              {myEntry.rank}
            </div>
            <div>
              <p className="text-sm font-semibold text-zinc-900">Your position</p>
              <p className="text-xs text-zinc-500">
                {myEntry.score.toLocaleString()} pts this period
              </p>
            </div>
          </div>
          <div>{delta(myEntry.deltaRank)}</div>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <EmptyCard
          icon={<Trophy size={22} />}
          title="No data yet"
          description="Leaderboard rankings will appear once there is activity in this period."
        />
      ) : (
        <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm overflow-hidden">
          {entries.map((e, idx) => (
            <div
              key={e.customerId}
              className={`flex items-center gap-3 px-4 py-3 border-b border-zinc-50 last:border-0 ${
                e.isCurrentUser ? 'bg-[--color-primary]/5' : idx < 3 ? 'bg-amber-50/40' : ''
              }`}
            >
              {/* Rank badge */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  e.rank === 1
                    ? 'bg-amber-400 text-white'
                    : e.rank === 2
                      ? 'bg-zinc-300 text-zinc-700'
                      : e.rank === 3
                        ? 'bg-amber-700/60 text-white'
                        : 'bg-zinc-100 text-zinc-600'
                }`}
              >
                {e.rank <= 3 ? ['🥇', '🥈', '🥉'][e.rank - 1] : e.rank}
              </div>
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-medium text-zinc-600 flex-shrink-0 overflow-hidden">
                {e.avatarUrl ? (
                  <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  initials(e.firstName, e.lastName)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium truncate ${e.isCurrentUser ? 'text-[--color-primary]' : 'text-zinc-900'}`}
                >
                  {e.isCurrentUser ? 'You' : fullName(e.firstName, e.lastName)}
                </p>
              </div>
              <div className="text-right shrink-0 flex items-center gap-3">
                {delta(e.deltaRank)}
                <p className="text-sm font-bold text-zinc-800 tabular-nums">
                  {e.score.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ReferralTab ──────────────────────────────────────────────────────────────

function ReferralTab({ programId }: { programId: string }) {
  const [data, setData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api
      .get<ReferralData>(`/customer/loyalty/referral?programId=${programId}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post('/customer/loyalty/referral/generate', { programId });
      load();
    } catch {
      /* ignore */
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!data?.referralCode) return;
    navigator.clipboard.writeText(data.referralCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    if (!data?.referralCode) return;
    if (navigator.share) {
      navigator
        .share({
          title: 'Join with my referral code',
          text: `Use my code ${data.referralCode} to sign up and earn bonus points!`,
        })
        .catch(() => {});
    } else {
      handleCopy();
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-zinc-500">
          Invite friends using your personal referral code and earn bonus points when they sign up.
        </p>
      </div>

      {/* Referral code card */}
      <div className="bg-gradient-to-br from-sky-50 to-indigo-50 border border-sky-100 rounded-2xl p-6 text-center space-y-4">
        {data?.referralCode ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-400">
              Your referral code
            </p>
            <div className="inline-flex items-center gap-3 bg-white rounded-xl px-5 py-3 shadow-sm border border-sky-100">
              <span className="text-2xl font-extrabold tracking-widest text-zinc-900 font-mono">
                {data.referralCode}
              </span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-zinc-200 text-sm font-medium text-zinc-700 hover:border-zinc-300 transition-colors shadow-sm"
              >
                {copied ? (
                  <CheckCircle2 size={14} className="text-emerald-500" />
                ) : (
                  <Copy size={14} />
                )}
                {copied ? 'Copied!' : 'Copy code'}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[--color-primary] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
              >
                <Share2 size={14} />
                Share
              </button>
            </div>
            <p className="text-xs text-sky-600">
              {data.referralCount} friend{data.referralCount !== 1 ? 's' : ''} joined using your
              code
            </p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-sky-100 flex items-center justify-center">
              <Users size={26} className="text-sky-500" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900">No referral code yet</p>
              <p className="text-sm text-zinc-500 mt-0.5">
                Generate your personal code and start inviting friends
              </p>
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[--color-primary] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Generate my code
            </button>
          </>
        )}
      </div>

      {/* Referred by */}
      {data?.referredByCode && (
        <div className="flex items-center gap-2 text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-xl px-4 py-2.5">
          <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
          You joined using referral code{' '}
          <code className="font-mono text-zinc-700 font-semibold">{data.referredByCode}</code>
        </div>
      )}

      {/* Referral history */}
      {data?.referrals && data.referrals.length > 0 && (
        <section>
          <SectionTitle>Friends who joined</SectionTitle>
          <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50">
            {data.referrals.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 text-xs font-medium flex-shrink-0">
                  {initials(r.firstName, r.lastName)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">
                    {fullName(r.firstName, r.lastName)}
                  </p>
                </div>
                <p className="text-xs text-zinc-400 shrink-0">{fmtYear(r.createdAt)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type TabKey = 'overview' | 'interests' | 'contest' | 'badges' | 'leaderboard' | 'referral';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Star size={14} /> },
  { key: 'interests', label: 'Interests', icon: <Tag size={14} /> },
  { key: 'contest', label: 'Gioca & Vinci', icon: <Play size={14} /> },
  { key: 'badges', label: 'Badges', icon: <Award size={14} /> },
  { key: 'leaderboard', label: 'Leaderboard', icon: <Trophy size={14} /> },
  { key: 'referral', label: 'Referral', icon: <Users size={14} /> },
];

export function LoyaltyPage() {
  const branding = useBranding();
  const [data, setData] = useState<LoyaltyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [redeemMsg, setRedeemMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [selectedAccountIdx, setSelectedAccountIdx] = useState(0);
  const [tab, setTab] = useState<TabKey>('overview');

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<LoyaltyData>('/customer/loyalty')
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load loyalty data. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleRedeem = async (reward: LoyaltyReward, account: LoyaltyAccount) => {
    setRedeemingId(reward.id);
    setRedeemMsg(null);
    try {
      const res = await api.post<{ redeemed: boolean; rewardName: string; pointsSpent: number }>(
        '/customer/loyalty/redeem',
        { programId: account.programId, rewardId: reward.id },
      );
      setRedeemMsg({
        ok: true,
        text: `Redeemed "${res.data.rewardName}" for ${res.data.pointsSpent} pts!`,
      });
      load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Redemption failed';
      const friendly: Record<string, string> = {
        insufficient_points: 'Not enough points for this reward.',
        reward_not_found: 'This reward is no longer available.',
        reward_out_of_stock: 'This reward is out of stock.',
        not_enrolled: 'You are not enrolled in this program.',
      };
      setRedeemMsg({ ok: false, text: friendly[msg] ?? msg });
    } finally {
      setRedeemingId(null);
    }
  };

  const account = data?.accounts?.[selectedAccountIdx] ?? null;
  const programId = account?.programId ?? '';

  return (
    <AppLayout>
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-zinc-400" />
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-600">
          {error}
        </div>
      ) : !data?.enrolled ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center">
            <Star size={28} className="text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-zinc-900">No loyalty program</h2>
          <p className="text-sm text-zinc-500 max-w-xs">
            You are not currently enrolled in any loyalty program. Contact support or visit a store
            to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Loyalty</h1>
              <p className="text-sm text-zinc-500 mt-0.5">Earn points, unlock badges, win prizes</p>
            </div>
            <button
              onClick={load}
              className="p-2 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Program tabs (multi-program) */}
          {(data.accounts?.length ?? 0) > 1 && (
            <div className="flex gap-2 flex-wrap">
              {data.accounts.map((acc, i) => (
                <button
                  key={acc.id}
                  onClick={() => setSelectedAccountIdx(i)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    i === selectedAccountIdx
                      ? 'border-[--color-primary] bg-[--color-primary]/10 text-[--color-primary]'
                      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  {acc.programName ?? `Program ${i + 1}`}
                </button>
              ))}
            </div>
          )}

          {/* Tab navigation */}
          <div className="flex items-center gap-0.5 border-b border-zinc-100 overflow-x-auto pb-px">
            {TABS.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === key
                    ? 'border-[--color-primary] text-[--color-primary]'
                    : 'border-transparent text-zinc-400 hover:text-zinc-700'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {account && (
            <>
              {tab === 'overview' && (
                <OverviewTab
                  data={data}
                  account={account}
                  branding={branding}
                  redeemingId={redeemingId}
                  redeemMsg={redeemMsg}
                  onRedeem={handleRedeem}
                  onDismissMsg={() => setRedeemMsg(null)}
                  onSwitchTab={setTab}
                />
              )}
              {tab === 'interests' && <InterestsTab programId={programId} />}
              {tab === 'contest' && (
                <ContestTab programId={programId} balance={account.pointsBalance} />
              )}
              {tab === 'badges' && <BadgesTab programId={programId} />}
              {tab === 'leaderboard' && <LeaderboardTab programId={programId} />}
              {tab === 'referral' && <ReferralTab programId={programId} />}
            </>
          )}
        </div>
      )}
    </AppLayout>
  );
}
